<template>
  <div id="SectionFeatureList">
    <UIContainer class="SectionFeatures__container">
      <SectionTitle class="SectionFeatures__title">
        <template #heading>Ecosystem</template>
        <template #title> Modular approach to sustainability </template>
        <template #description>
          Start with the platform, add expert lift when needed - same data,
          same<br />
          evidence, zero rework.
        </template>
      </SectionTitle>

      <div class="SectionFeatures__grid">
        <ul class="SectionFeatures__features">
          <li
            v-for="(feature, index) in features"
            :key="feature.title"
            class="SectionFeatures__feature"
            :class="{
              'SectionFeatures__feature--highlighted': isHighlighted(index),
              'SectionFeatures__feature--collapsed': isCollapsed(index),
            }"
            @mouseenter="hoveredIndex = index"
            @mouseleave="hoveredIndex = null"
          >
            <h3 class="SectionFeatures__feature-title">
              {{ feature.title }}
            </h3>
            <p class="SectionFeatures__feature-description">
              {{ feature.description }}
            </p>
            <a
              v-if="feature.link"
              :href="feature.link"
              target="_blank"
              class="SectionFeatures__feature-link"
            >
              Learn more
              <UIIcon name="arrowLink" />
            </a>
          </li>
        </ul>

        <Graph
          class="SectionFeatures__image"
          :style="
            hoveredIndex !== null
              ? { [`--color-arrow-${hoveredIndex + 1}`]: '#03604b' }
              : {}
          "
        />
        <!--<img
          class="SectionFeatures__image"
          src="./images/featureList/graph.svg"
          alt="Data integration dashboard"
          height="600"
        />-->
      </div>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import Graph from './images/featureList/graph.vue'

interface Feature {
  title: string
  description: string
  link?: string
}

const hoveredIndex = ref<number | null>(null)

const features: Feature[] = [
  {
    title: 'ESG Data Platform',
    description: 'The foundation for your sustainability program.',
  },
  {
    title: 'GHG Accounting',
    description: 'Your engine for accurate, compliant.',
  },
  {
    title: 'XBRL Tagging',
    description: 'Automated tagging for regulatory filings.',
  },
  {
    title: 'Consulting (SBTi/CSRD/DMA)',
    description: 'Expert guidance to navigate the complex.',
  },
  {
    title: 'Sustainability Academy',
    description: 'Empower your team with expert knowledge.',
  },
]

/**
 * Determines if a feature should be highlighted
 * First feature is highlighted when no other feature is hovered
 */
const isHighlighted = (index: number): boolean => {
  return index === 0 && hoveredIndex.value === null
}

/**
 * Determines if a feature should be collapsed
 * First feature is collapsed when another feature is hovered
 */
const isCollapsed = (index: number): boolean => {
  return index === 0 && hoveredIndex.value !== null && hoveredIndex.value !== 0
}
</script>

<style lang="scss" scoped>
#SectionFeatureList {
  position: relative;

  .SectionFeatures__title {
    max-width: 48rem;
  }

  .SectionFeatures__grid {
    margin-top: 1.25rem;
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    @include md {
      grid-template-columns: 450px 1fr;
      gap: 4rem;
    }
  }

  .SectionFeatures__image {
    max-height: 416px;
    height: auto;
    width: 100%;
    display: none;

    @include md {
      display: block;
    }
  }

  .SectionFeatures__features {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .SectionFeatures__feature {
    @include trans(0.5s);
    border-left: 4px solid $color-ui-gray-cool-100;
    padding: 1rem 1.5rem;
    // transition: border-color 2s ease;
    cursor: pointer;

    &:hover {
      border-color: $color-ui-brand-500;

      .SectionFeatures__feature-description {
        max-height: 100px;
        opacity: 1;
        margin-top: 0.25rem;
      }
      .SectionFeatures__feature-link {
        max-height: 50px;
        opacity: 1;
        margin-top: 1rem;
      }
    }

    // Highlighted state (first feature when no hover)
    &--highlighted {
      border-color: $color-ui-brand-500;

      .SectionFeatures__feature-description {
        max-height: 100px;
        opacity: 1;
        margin-top: 0.25rem;
      }
      .SectionFeatures__feature-link {
        max-height: 50px;
        opacity: 1;
        margin-top: 1rem;
      }
    }

    // Collapsed state (first feature when another is hovered)
    &--collapsed {
      border-color: $color-ui-gray-cool-100;

      .SectionFeatures__feature-description {
        max-height: 0;
        opacity: 0;
        margin-top: 0;
      }
      .SectionFeatures__feature-link {
        max-height: 0;
        opacity: 0;
        margin-top: 0;
      }
    }
  }

  .SectionFeatures__feature-title {
    @include font-size-18px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
  }

  .SectionFeatures__feature-description {
    @include font-size-16px();
    @include trans(0.5s);
    margin-top: 0.25rem;
    color: $color-ui-gray-cool-600;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition:
      opacity 0.5s ease,
      max-height 0.5s ease,
      margin-top 0.5s ease;
  }

  .SectionFeatures__feature-link {
    @include font-size-16px();
    @include trans(0.5s);
    display: flex;
    font-weight: 600;
    margin-top: 0;
    gap: 0.5rem;
    align-items: center;
    color: $color-ui-brand-700;
    text-decoration: none;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition:
      opacity 0.5s ease,
      max-height 0.5s ease,
      margin-top 0.5s ease;
  }
}
</style>
